import React from 'react';
import { BloodworkAnalysis } from '../../models/BloodworkAnalysis';
import { analyzeMvx, MvxFactorResult } from '../../models/MetabolicVulnerability';
import { MVX_ROOT_CAUSES, MvxRootCauseEntry, rootCauseTitle } from '../../models/mvxRootCauses';
import { inputsFromBloodwork } from './metabolic-vulnerability-insights';
import { renderTextRun } from './further-considerations-section';

/**
 * MVX "Possible Root Causes" tab — why a factor is high or low.
 *
 * Renders in the house Causes-tab markup: a `consideration-title` per row, then
 * a `<ul>` of bullets. Two rows per entry, Functional then Medical. Bullet text
 * goes through the house `renderTextRun`, so an inline "— PMID: n, m" linkifies
 * exactly as it does in every other section's Causes tab and ▲/▼ still render
 * as icons.
 *
 * SHOWS ONLY WHAT FIRED. An entry appears when a factor the client actually has
 * a value for is off target ON THAT SIDE, which is the same rule the house tab
 * follows — a Causes list for a marker sitting in range is noise. The three
 * index scores never appear at all; see the mvxRootCauses header.
 *
 * Entry order follows the bars (GlycA, Small HDL-P, citrate, the BCAAs), so a
 * practitioner reading down the Results panel meets the causes in the same
 * order.
 */
interface Props {
  bloodworkAnalysis: BloodworkAnalysis;
}

/**
 * The entries to show, in bar order. An entry fires when ANY of its markers is
 * entered and sitting on that entry's side of the target band — which is what
 * lets the three BCAAs share one entry: one of them out is enough, exactly as
 * the Insights bullet rules read them.
 */
export function firedRootCauses(factors: MvxFactorResult[]): MvxRootCauseEntry[] {
  const state = (id: string) => factors.find(f => f.id === id && f.hasValue);
  return MVX_ROOT_CAUSES.filter(entry =>
    entry.markers.some(id => {
      const f = state(id);
      return !!f && f.state === entry.direction;
    }),
  );
}

export class MvxRootCausesSection extends React.Component<Props> {
  static shouldRender(ba: BloodworkAnalysis | undefined): boolean {
    return analyzeMvx(inputsFromBloodwork(ba)).anyEntered;
  }

  renderRow(entry: MvxRootCauseEntry, row: 'functional' | 'medical', key: string) {
    return (
      <span key={key}>
        <span className="consideration-title">{rootCauseTitle(entry, row)}</span>
        <ul>
          {entry[row].map((bullet, i) => (
            <li key={i}>{renderTextRun(bullet, `${key}-${i}`)}</li>
          ))}
        </ul>
      </span>
    );
  }

  render() {
    const result = analyzeMvx(inputsFromBloodwork(this.props.bloodworkAnalysis));
    const fired = firedRootCauses(result.factors);

    if (fired.length === 0) {
      return (
        <p className="text-muted">
          Every measured factor is inside its target range, so there is nothing to look behind. Possible root
          causes appear here when a factor reads high or low.
        </p>
      );
    }

    return (
      <span>
        {fired.map((entry, i) => (
          <span key={i}>
            {this.renderRow(entry, 'functional', `f-${i}`)}
            {this.renderRow(entry, 'medical', `m-${i}`)}
          </span>
        ))}
      </span>
    );
  }
}

export default MvxRootCausesSection;
